import artistBioCopy from '../data/artistBioCopy.json';

export const TRUEVINED_BIO = artistBioCopy.aboutBio;

export const TRUEVINED_ACHIEVEMENTS = [
  "Thousands of ears hearing about Jesus",
  "#26 Pop iTunes (Believer Remix)",
  "#47 Electronic iTunes (OTW Instrumental Remix)",
  'Featured on T.I. & Tiny\'s "Friends and Family Hustle"',
  "Spotify Editorial: New Music Friday Christian",
  "Spotify Editorial: Northern Bars",
  "Spotify Editorial: Indie Pop Chillout",
  "VH1 sync placement (Ah Lawdy)",
  "2025 releases: TRUST EP, Lofi King and Faith Over Feelings",
  "2026 releases: Heavenly Social and Swish Swash",
];

export const TRUEVINED_SONG_MAPPINGS: Record<string, string> = {
  Believer: "Believer Remix",
  "Ah Lawdy": "Ah Lawdy",
  "OTW instrumental remix": "OTW Remix",
  "Grace Remix": "Grace Remix",
  "100 percent": "100 Percent",
  "TRUST EP": "TRUST",
  "Lofi King": "Lofi King",
  "Heavenly Social": "Heavenly Social",
  "Swish Swash": "Swish Swash",
  "Faith Over Feelings": "Faith Over Feelings",
  "Afrobeat Remix": "Heavenly Social - Afrobeat Remix",
};

export const TRUEVINED_BIO_SONG_MENTIONS = [
  "Believer",
  "Ah Lawdy",
  "OTW instrumental remix",
  "Grace Remix",
  "100 percent",
  "TRUST EP",
  "Lofi King",
  "Heavenly Social",
  "Swish Swash",
  "Faith Over Feelings",
  "Afrobeat Remix",
];

export function cleanBioText(rawBio?: string): string {
  if (!rawBio) return "";
  return rawBio
    .replace(/<[^>]*>/g, "")
    .replace(/Read more about.*?on Last\.fm\.?/gi, "")
    .trim();
}

export interface BioData {
  bio: string;
  achievements: string[];
  songMappings: Record<string, string>;
  bioSongMentions: string[];
}

interface ArtistBioSource {
  name?: string;
  genres?: string[];
  followers?: { total?: number };
  topTracks?: Array<{ name?: string }>;
}

interface LastFmBioSource {
  tags?: string[];
  listeners?: string | number;
  playcount?: string | number;
  similarArtists?: Array<{ name?: string }>;
}

interface HomepageBioRelease {
  id?: string;
  name?: string;
  release_date?: string;
  album_group?: string;
  artists?: Array<{ name?: string }>;
}

const fillBioTemplate = (template: string, values: Record<string, string>) =>
  template.replace(/\{(\w+)\}/g, (_, key) => values[key] || "");

export function deriveHomepageBio(
  artistName?: string,
  artistData?: ArtistBioSource | null,
  releases: HomepageBioRelease[] = [],
  compact = false
): string {
  const name = artistData?.name?.trim() || artistName?.trim();
  if (!name) return "";
  const genres = uniqueWords(artistData?.genres || []).slice(0, 3);
  const intro = name.toLowerCase() === "truevined"
    ? artistBioCopy.heroIntro.split(/(?<=\.)\s+/).slice(0, 2).join(" ")
    : fillBioTemplate(
      genres.length ? artistBioCopy.templates.genericGenreIntroTemplate : artistBioCopy.templates.genericMinimalIntroTemplate,
      { name, genres: formatList(genres) }
    );
  if (compact) return intro;
  const ownReleases = releases.filter(release => {
    if (release.album_group === "appears_on" || !release.name?.trim()) return false;
    return !release.artists?.length || release.artists.some(artist => artist.name?.toLowerCase() === name.toLowerCase());
  }).sort((a, b) => (b.release_date || "").localeCompare(a.release_date || ""));
  const releaseNames = uniqueWords(ownReleases.map(release => release.name)).slice(0, 3);
  const tracks = uniqueWords(artistData?.topTracks?.map(track => track.name) || []).slice(0, 3);
  const bridge = releaseNames.length
    ? fillBioTemplate(artistBioCopy.templates.catalogBridgeTemplate, { releases: formatList(releaseNames) })
    : tracks.length
      ? fillBioTemplate(artistBioCopy.templates.topTracksTemplate, { tracks: formatList(tracks) })
      : "";
  return [intro, bridge].filter(Boolean).join(" ");
}

const uniqueWords = (values: Array<string | undefined | null>) =>
  Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

const formatList = (values: string[]) => {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
};

export function deriveArtistBio(
  artistName?: string,
  artistData?: ArtistBioSource | null,
  lastFmData?: LastFmBioSource | null
): string {
  const name = artistData?.name || artistName;
  if (!name) return "";

  const genres = uniqueWords([...(artistData?.genres || []), ...(lastFmData?.tags || [])]).slice(
    0,
    4
  );
  const topTracks = uniqueWords((artistData?.topTracks || []).map((track) => track.name)).slice(
    0,
    4
  );
  const similarArtists = uniqueWords(
    (lastFmData?.similarArtists || []).map((artist) => artist.name)
  ).slice(0, 3);
  const followers = Number(artistData?.followers?.total || 0);
  const listeners = Number(lastFmData?.listeners || 0);
  const playcount = Number(lastFmData?.playcount || 0);

  const sentences: string[] = [];
  const genreText = formatList(genres);

  sentences.push(
    genreText
      ? `${name} is an artist whose catalog moves through ${genreText}.`
      : `${name} is an artist with a growing catalog shaped by the music they release and the listeners who return to it.`
  );

  if (topTracks.length > 0) {
    sentences.push(
      `Songs like ${formatList(topTracks)} give a quick window into the releases fans are finding first.`
    );
  }

  const audienceParts = [
    followers > 0 ? `${followers.toLocaleString()} Spotify followers` : "",
    listeners > 0 ? `${listeners.toLocaleString()} Last.fm listeners` : "",
    playcount > 0 ? `${playcount.toLocaleString()} Last.fm plays` : "",
  ].filter(Boolean);

  if (audienceParts.length > 0) {
    sentences.push(`Their audience footprint includes ${formatList(audienceParts)}.`);
  }

  if (similarArtists.length > 0) {
    sentences.push(
      `Listeners also connect ${name}'s music with artists like ${formatList(similarArtists)}.`
    );
  }

  sentences.push(
    `Explore ${name}'s songs, albums, videos, and latest releases to hear the full story unfold.`
  );

  return sentences.join("\n\n");
}

export function getBioData(
  artistName?: string,
  lastFmBio?: string,
  lastFmError?: boolean,
  artistData?: ArtistBioSource | null,
  lastFmData?: LastFmBioSource | null
): BioData {
  const isTruevined = artistName?.toLowerCase() === "truevined";
  const cleanedBio = cleanBioText(lastFmBio);

  if (isTruevined) {
    return {
      bio: TRUEVINED_BIO,
      achievements: TRUEVINED_ACHIEVEMENTS,
      songMappings: TRUEVINED_SONG_MAPPINGS,
      bioSongMentions: TRUEVINED_BIO_SONG_MENTIONS,
    };
  }

  return {
    bio: cleanedBio || deriveArtistBio(artistName, artistData, lastFmData),
    achievements: [],
    songMappings: {},
    bioSongMentions: [],
  };
}
